import TodayOpenItems from './TodayOpenItems'
import { useContextualNavigation } from './navigation/useRead'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/useAuth'
import { apiFetch } from '../lib/api'
import { normaliseCounterparty } from '../lib/chaseCopy'
import { HelpDot } from './guidance/Guidance'
import { EmptyState, ErrorState, Skeleton } from './state'
import Sentence, { LinkedSentence } from './Sentence'
import DocumentLink from './documents/DocumentLink'
import { documentReaderHref, type DocumentRef } from '../lib/documentReader'
import FundSelector from './FundSelector'
import { useFundScope } from './fundScope'
import { fetchFundLedgers } from './ledger'
import { breaksWords, openWords, owedWords, provenWords, type FundLedger } from './ledgerWords'
import {
  dayHeading,
  localDayIso,
  rangeLabel,
  signInParts,
  waitedLabel,
  whenLabel,
} from './homeTime'
import DocumentActivity from './DocumentActivity'
import './home.css'

/* Today — the fund day sheet (Ale ruling 2026-09-03, "today a"; window +
 * doors + NEEDS YOU + sign-off block, Tier-1 B3 2026-09-04).
 *
 * ONE BLOCK PER FUND. Today never lists to-dos (the workbench does); it
 * shows WHAT CHANGED on each fund since the officer's last COMPLETED
 * session and WHAT NEEDS THE OFFICER there. The header figures are the
 * fund's LEDGER (B1, Ale 2026-09-04) — embedded per fund in
 * /api/home/day-sheet, the same read every other screen counts from — so
 * the numbers agree by construction. A manager gets AWAITING YOUR
 * SIGN-OFF on top. Day-0 funds and quiet funds collapse to one row each.
 * Every line is a door with an id. No inner scroller: the page scrolls.
 * No headline count, no ring, no posture wall, no workbench rollup. */

type DoorType =
  | 'document'
  | 'documents'
  | 'task'
  | 'request'
  | 'fund_contact'
  | 'trade'
  | 'position'
  | 'instrument'
  | 'entity'

interface Door {
  type: DoorType
  id: string | null
  fund_id: string | null
}

type ChangeKind = 'document_read' | 'break_appeared' | 'booked' | 'request_sent'
type UrgentKind = 'request_overdue' | 'break_open'
type Values = Record<string, string | number | string[] | null>

interface ChangeLine {
  document_ref?: DocumentRef | null
  kind: ChangeKind
  at: string
  sentence_meaning: string
  values: Values
  door: Door
  arrow: 'position' | 'instrument' | null
  /** Identical events fold: how many, and the oldest (`from_at`). */
  count: number
  from_at: string | null
}

interface UrgentLine {
  kind: UrgentKind
  days: number | null
  sentence_meaning: string
  values: Values
  door: Door
}

export interface SignoffRow {
  fund_id: string
  fund_name: string
  task_id: string
  kind: 'verification_signoff'
  since: string
  sentence_meaning: string
  values: Values
  door: Door
}

interface FundSheet {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  /** The fund's ledger — proven · owed · breaks · to decide, one read. */
  ledger: FundLedger
  day0: boolean
  /** The ledger's to_book: decks at the booking stage naming this fund. */
  proposals_waiting: number
  waiting_documents: string[]
  waiting_document_refs?: DocumentRef[]
  last_document: { name: string; date: string; document_ref?: DocumentRef | null } | null
  changes: ChangeLine[]
  urgent: UrgentLine[]
}

export interface DaySheet {
  today: string
  since: string
  since_kind: 'last_completed_session' | 'fallback_24h' | 'query'
  last_sign_in: string | null
  funds: FundSheet[]
  changes: number
  attention: number
  awaiting_signoff: SignoffRow[] | null
  generated_at: string
}

type TaskFamily = 'reconciliation' | 'ownership-verification'

/** Where a line opens — always the THING the line names, never a queue:
 *  the document page, the task pane, the record focused on the object,
 *  the request's card on the chase page, the fund's contact capture. */
function doorHref(door: Door, family: TaskFamily = 'reconciliation'): string {
  const fund = door.fund_id ? `fund=${encodeURIComponent(door.fund_id)}` : ''
  switch (door.type) {
    case 'document':
      return door.id
        ? documentReaderHref(door.id, { fundId: door.fund_id, returnTo: '/v2' })
        : '/v2/traces'
    case 'documents':
      return '/v2/traces'
    case 'request': {
      const q = [fund, door.id ? `request=${encodeURIComponent(door.id)}` : '']
        .filter(Boolean)
        .join('&')
      return `/v2/workbench/chase-request${q ? `?${q}` : ''}`
    }
    case 'fund_contact':
      return `/v2/workbench/chase-request?${fund ? `${fund}&` : ''}contact=add`
    case 'task':
      return door.id
        ? `/v2/workbench/${family}/task/${encodeURIComponent(door.id)}`
        : `/v2/workbench/${family}`
    default: {
      if (!door.fund_id) return '/v2/funds'
      const focus = door.id ? `&focus=${encodeURIComponent(`${door.type}:${door.id}`)}` : ''
      return `/v2/funds/${encodeURIComponent(door.fund_id)}?tab=record${focus}`
    }
  }
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The read path already turns a request stored with no contact into the
 *  no-counterparty meaning with a `fund_contact` door. This keeps the
 *  same rule for any line that still carries the write path's sentinel
 *  (copy path, Ale 2026-09-04): "Unknown" never renders. */
const REQUEST_MEANINGS = new Set(['home.change.request_sent', 'home.urgent.request_overdue'])

function requestLine(
  meaning: string,
  values: Values,
  door: Door,
  f: FundSheet
): { meaning: string; values: Values; door: Door } {
  if (!REQUEST_MEANINGS.has(meaning)) return { meaning, values, door }
  const counterparty = normaliseCounterparty(
    typeof values.counterparty === 'string' ? values.counterparty : null
  )
  if (counterparty) return { meaning, values: { ...values, counterparty }, door }
  const { counterparty: _dropped, ...rest } = values
  void _dropped
  return {
    meaning: `${meaning}.nocounterparty`,
    values: { ...rest, fund: f.name },
    door: { type: 'fund_contact', id: f.id, fund_id: f.id },
  }
}

function FundName({ f }: { f: FundSheet }) {
  return (
    <span className="tf-name">
      {f.name}
      {f.legal_form || f.jurisdiction ? (
        <small>{[f.legal_form, f.jurisdiction].filter(Boolean).join(' · ')}</small>
      ) : null}
    </span>
  )
}

/** The fund's figures — the LEDGER's, in the ledger's own words (B1):
 *  proven of demanded · owed · breaks, and the other open work when
 *  there is any. The same figure every other screen prints. */
function Figures({ f }: { f: FundSheet }) {
  const l = f.ledger
  return (
    <span className="tf-figs mono" data-testid="today-fund-figures">
      <span>{provenWords(l.owed)}</span>
      <span className="sep"> · </span>
      <span className={l.owed.total > 0 ? 'owed' : 'zero'}>{owedWords(l.owed)}</span>
      <span className="sep"> · </span>
      <span className={l.breaks.open > 0 ? 'brk' : 'zero'}>{breaksWords(l.breaks)}</span>
      {l.to_decide > 0 ? (
        <>
          <span className="sep"> · </span>
          <span data-testid="today-to-decide">{l.to_decide} to decide</span>
        </>
      ) : null}
      {l.to_book > 0 ? (
        <>
          <span className="sep"> · </span>
          <span data-testid="today-to-book">{l.to_book} to book</span>
        </>
      ) : null}
    </span>
  )
}

/** Legacy response fallback: unlinked names are labels, never invented IDs. */
function documentsDatum(names: string[]): string[] {
  if (names.length <= 3) return names
  return [...names.slice(0, 2), `${names.length - 2} more`]
}

function QuietSentence({ f, since }: { f: FundSheet; since: string }) {
  return (
    <LinkedSentence
      slot="document"
      href={
        f.last_document?.document_ref
          ? documentReaderHref(f.last_document.document_ref.document_id, {
              fundId: f.id,
              returnTo: '/v2',
            })
          : null
      }
      documentRef={f.last_document?.document_ref}
      className="tf-quiet"
      data-testid="today-quiet"
      meaning={f.last_document ? 'home.quiet' : 'home.quiet_nodoc'}
      values={
        f.last_document
          ? {
              since: localDayIso(since),
              document: f.last_document.document_ref?.display_title ?? 'Document',
              date: f.last_document.date,
            }
          : { since: localDayIso(since) }
      }
    />
  )
}

/** A fund that collapses to ONE row: day 0 with a backlog (the backlog is
 *  the fact) or quiet (nothing changed, nothing needs you). */
function FundRow({ f, since, backlog }: { f: FundSheet; since: string; backlog: boolean }) {
  return (
    <section
      className={`vcard today-fund today-row${backlog ? ' day0' : ' quiet'}`}
      data-testid="today-fund-block"
      data-fund={f.id}
      data-row={backlog ? 'day0' : 'quiet'}
    >
      <Link to={`/v2/funds/${encodeURIComponent(f.id)}`}>
        <FundName f={f} />
      </Link>
      <span className="tr-sep">·</span>
      {backlog ? (
        <LinkedSentence
          slot="documents"
          href="/v2"
          documentRefs={f.waiting_document_refs}
          documentLimit={(f.waiting_document_refs?.length ?? 0) > 3 ? 2 : 3}
          documentContext={{ fundId: f.id, returnTo: '/v2' }}
          className="tr-day0 mono"
          data-testid="today-day0"
          meaning="home.day0"
          values={{
            stage: 'day 0',
            proposals: f.proposals_waiting,
            documents: f.waiting_document_refs?.length
              ? documentsDatum(
                  f.waiting_document_refs.map((ref) => ref.display_title ?? 'Document')
                )
              : documentsDatum(f.waiting_documents.map(() => 'Document')),
          }}
        />
      ) : (
        <span className="tr-quiet">
          <Figures f={f} />
          <span className="tr-sep">·</span>
          <QuietSentence f={f} since={since} />
        </span>
      )}
      <Link className="tf-open door" to={`/v2/funds/${encodeURIComponent(f.id)}`}>
        Open ›
      </Link>
    </section>
  )
}

/** Keep document popups and Record doors as sibling links, never nested anchors. */
function ChangeDoor({
  change,
  door,
  children,
}: {
  change: ChangeLine
  door: Door
  children: ReactNode
}) {
  const attrs = {
    className: 'tf-ln',
    'data-testid': 'today-change',
    'data-kind': change.kind,
    'data-count': change.count,
  }
  if (door.type === 'document' && door.id)
    return (
      <DocumentLink
        {...attrs}
        document={change.document_ref ?? { document_id: door.id }}
        context={{ fundId: door.fund_id, returnTo: '/v2' }}
      >
        {children}
      </DocumentLink>
    )
  if (change.document_ref) return <div {...attrs}>{children}</div>
  return (
    <Link {...attrs} to={doorHref(door)} title="open ›">
      {children}
    </Link>
  )
}

function FundBlock({ f, today, since }: { f: FundSheet; today: string; since: string }) {
  const backlog = f.day0 && f.proposals_waiting > 0
  const needs = f.urgent.length > 0
  if (backlog || (f.changes.length === 0 && !needs))
    return <FundRow f={f} since={since} backlog={backlog} />
  return (
    <section className="vcard today-fund" data-testid="today-fund-block" data-fund={f.id}>
      <div className="tf-head">
        <FundName f={f} />
        <Figures f={f} />
        <Link className="tf-open door" to={`/v2/funds/${encodeURIComponent(f.id)}`}>
          Open ›
        </Link>
      </div>
      <div className={`tf-body${needs ? '' : ' solo'}`}>
        <div className="tf-col" data-testid="today-changed">
          <div className="tf-ch">
            Changed{' '}
            <span data-testid="today-changed-since">
              — {plural(f.ledger.changed_since.count, 'change', 'changes')}
            </span>
          </div>
          {f.changes.length === 0 ? (
            <div className="tf-empty">
              <QuietSentence f={f} since={since} />
            </div>
          ) : (
            f.changes.map((c, i) => {
              const line = requestLine(c.sentence_meaning, c.values, c.door, f)
              const folded = c.count > 1 && c.from_at
              return (
                <ChangeDoor key={`${c.kind}-${c.at}-${i}`} change={c} door={line.door}>
                  <span className="tf-k" aria-hidden>
                    {c.kind === 'booked' ? (
                      <span className={`tf-arr ${c.arrow ?? 'position'}`}>➜</span>
                    ) : (
                      <span className={`tf-dot ${c.kind}`} />
                    )}
                  </span>
                  <span className="tf-txt">
                    {c.count > 1 ? (
                      <Sentence
                        className="tf-x mono"
                        data-testid="today-fold"
                        meaning="home.fold"
                        values={{ count: c.count }}
                      />
                    ) : null}
                    {c.document_ref && line.door.type !== 'document' ? (
                      <LinkedSentence
                        meaning={line.meaning}
                        values={line.values}
                        slot="document"
                        documentRef={c.document_ref}
                        href={documentReaderHref(c.document_ref.document_id, {
                          fundId: f.id,
                          returnTo: '/v2',
                        })}
                      />
                    ) : (
                      <Sentence meaning={line.meaning} values={line.values} />
                    )}
                    {line.door.type === 'fund_contact' ? (
                      <span className="tf-go door" data-testid="today-contact-door">
                        › add
                      </span>
                    ) : null}
                  </span>
                  <span className="tf-t mono" data-testid="today-when">
                    {folded ? rangeLabel(c.from_at as string, c.at, today) : whenLabel(c.at, today)}
                  </span>
                  {c.document_ref && line.door.type !== 'document' ? (
                    <Link
                      className="tf-chev door"
                      to={doorHref(line.door)}
                      aria-label="Open record"
                    >
                      ›
                    </Link>
                  ) : (
                    <span className="tf-chev door" aria-hidden>
                      ›
                    </span>
                  )}
                </ChangeDoor>
              )
            })
          )}
        </div>
        {needs ? (
          <div className="tf-col" data-testid="today-needs-you">
            <div className="tf-ch need">Needs you</div>
            {f.urgent.map((u, i) => {
              const line = requestLine(u.sentence_meaning, u.values, u.door, f)
              const contact = line.door.type === 'fund_contact'
              return (
                <Link
                  key={`${u.kind}-${i}`}
                  className={`tf-ln urg ${u.kind}`}
                  data-testid="today-urgent"
                  data-kind={u.kind}
                  to={doorHref(line.door)}
                >
                  <span className="tf-qb" aria-hidden />
                  <Sentence className="tf-txt" meaning={line.meaning} values={line.values} />
                  <span
                    className="tf-go door"
                    data-testid={contact ? 'today-contact-door' : undefined}
                  >
                    {contact ? '› add' : 'open ›'}
                  </span>
                </Link>
              )
            })}
          </div>
        ) : null}
      </div>
    </section>
  )
}

/** AWAITING YOUR SIGN-OFF — the manager's four-eyes home: one row per
 *  open sign-off across the funds, fund first, oldest first. */
function SignoffBlock({ rows, now }: { rows: SignoffRow[]; now: string }) {
  return (
    <section className="vcard today-so" data-testid="today-signoff">
      <div className="so-lab">
        Awaiting your sign-off <span className="so-n mono">· {rows.length}</span>
      </div>
      {rows.map((r) => (
        <Link
          key={r.task_id}
          className="so-row"
          data-testid="today-signoff-row"
          data-fund={r.fund_id}
          to={doorHref(r.door, 'ownership-verification')}
        >
          <span className="so-qb" aria-hidden />
          <Sentence className="so-txt" meaning={r.sentence_meaning} values={r.values} />
          <Sentence
            className="so-w mono"
            meaning="home.signoff.waited"
            values={{ waited: waitedLabel(r.since, now) }}
          />
          <span className="so-go door">open ›</span>
        </Link>
      ))}
    </section>
  )
}

export default function Home() {
  const capability = useContextualNavigation()
  if (capability.loading)
    return (
      <main className="detail" role="status">
        Loading Today…
      </main>
    )
  if (capability.error)
    return (
      <main className="detail">
        <ErrorState what="Today navigation" error={capability.error} />
      </main>
    )
  return capability.value?.enabled ? <TodayOpenItems /> : <LegacyHome />
}

function LegacyHome() {
  const { user } = useAuth()
  const manager = user?.role === 'manager' || user?.role === 'admin'
  // null = loading, 'error' = fetch failed. A failed fetch is never
  // rendered as a quiet day (the silent-blank family, atlas 2026-07-27).
  const [sheet, setSheet] = useState<DaySheet | 'error' | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  // Retry (state system): bumps the tick, which re-runs the effect — one
  // fresh read AND a fresh heartbeat clock, so the retry never doubles up
  // with a tick that was about to fire.
  const [tick, setTick] = useState(0)
  const [documentRefresh, setDocumentRefresh] = useState(0)
  useEffect(() => {
    let alive = true
    const pull = (background = false) => {
      apiFetch<DaySheet>('/api/home/day-sheet', { background })
        .then((d) => {
          if (alive) {
            setSheet(d)
            setFailure(null)
            setDocumentRefresh((n) => n + 1)
          }
        })
        .catch((e: unknown) => {
          if (!alive) return
          setFailure(e)
          setSheet((s) => (s && s !== 'error' ? s : 'error'))
        })
    }
    pull(false)
    // Heartbeat (Massimo 2026-08-30): Today refreshes itself — as
    // background, so the idle clock (lib/api.ts) does not move.
    const t = setInterval(() => pull(true), 15000)
    return () => {
      alive = false
      clearInterval(t)
    }
  }, [tick])
  const retry = () => {
    setSheet(null)
    setFailure(null)
    setTick((t) => t + 1)
  }

  /* ONE fund scope (Tier-2 selector ruling): the same control the
   * Workbench, the queues and the fund pages carry — the choice arrives
   * here and leaves here in `?fund=`. Scoped, Today shows that fund's
   * block alone and the strip counts what is on screen; unscoped, the
   * sheet's own totals stand. */
  const { fundId, setFund } = useFundScope()

  const loaded = sheet && sheet !== 'error' ? sheet : null
  const todayIso = loaded ? loaded.today : localDayIso(new Date().toISOString())
  const since = loaded && loaded.since_kind !== 'fallback_24h' ? signInParts(loaded.since) : null

  /* The selector lists EVERY fund on record, from the bulk ledger — the
   * same list, the same figures, as the Workbench and the queues. Today's
   * own sheet only carries the funds with something to say, and a menu
   * that quietly drops the quiet funds is the thing the ruling forbids. */
  const [ledgers, setLedgers] = useState<FundLedger[] | null>(null)
  useEffect(() => {
    let alive = true
    fetchFundLedgers()
      .then((l) => {
        if (alive) setLedgers(l.funds)
      })
      .catch(() => {
        if (alive) setLedgers(null)
      })
    return () => {
      alive = false
    }
  }, [])
  const selectorFunds = useMemo(
    () =>
      (ledgers ?? []).map((f) => ({
        id: f.fund_id,
        name: f.fund_name,
        open: f.total_open,
        sub: openWords(f),
      })),
    [ledgers]
  )
  const scoped = useMemo(
    () => (loaded ? (fundId ? loaded.funds.filter((f) => f.id === fundId) : loaded.funds) : []),
    [loaded, fundId]
  )
  /* The strip's figures: the sheet's own when every fund is shown, the
   * SHOWN funds' otherwise — the same figures their blocks print, so the
   * strip and the page can never disagree. */
  const strip = useMemo(() => {
    if (!loaded) return { changes: 0, attention: 0 }
    if (!fundId) return { changes: loaded.changes, attention: loaded.attention }
    return {
      changes: scoped.reduce((n, f) => n + f.ledger.changed_since.count, 0),
      attention: scoped.reduce((n, f) => n + f.urgent.length, 0),
    }
  }, [loaded, fundId, scoped])

  const allSignoffs = manager && loaded?.awaiting_signoff ? loaded.awaiting_signoff : []
  const signoffs = fundId ? allSignoffs.filter((r) => r.fund_id === fundId) : allSignoffs

  return (
    <main className="detail today">
      <div className="dhead today-strip">
        {/* Visible title = the date (Ale, fund day sheet); the accessible name
            keeps the page's identity ("Today") for navigation and specs. */}
        <h1 aria-label={`Today — ${dayHeading(todayIso)}`}>
          {dayHeading(todayIso)} <HelpDot k="surface.home" />
        </h1>
        {/* The fund selector rides the title line — the same control, in
            the same place, on every page (Tier-2 ruling). */}
        <FundSelector
          funds={selectorFunds}
          value={fundId}
          onChoose={setFund}
          loading={ledgers === null}
        />
        {loaded ? (
          <Sentence
            as="span"
            className="today-when"
            data-testid="today-since"
            data-since-kind={loaded.since_kind}
            meaning={since ? 'home.since_session' : 'home.since_fallback'}
            values={since ? { day: since.day, time: since.time } : {}}
          />
        ) : null}
        {loaded ? (
          <span className="today-cnt" data-testid="today-strip-counts">
            <span>
              Fund records<span className="sep"> · </span>
            </span>
            <Sentence meaning="home.strip.changes" values={{ changes: strip.changes }} />
            <span className="sep"> · </span>
            <Sentence meaning="home.strip.needs_you" values={{ attention: strip.attention }} />
            {manager && loaded.awaiting_signoff ? (
              <>
                <span className="sep"> · </span>
                <Sentence
                  data-testid="today-strip-signoff"
                  meaning="home.strip.signoff"
                  values={{ count: signoffs.length }}
                />
              </>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="dbody today-body">
        {loaded && (
          <DocumentActivity since={loaded.since} fundId={fundId} refreshKey={documentRefresh} />
        )}
        {sheet === null ? (
          /* One fund block's height: head + two columns of lines. */
          <Skeleton kind="cards" n={3} height={118} />
        ) : sheet === 'error' ? (
          <ErrorState what="day sheet" error={failure} onRetry={retry} testId="today-error" />
        ) : sheet.funds.length === 0 ? (
          <EmptyState
            sentence="No funds on record yet."
            door={{ label: 'Funds ›', to: '/v2/funds' }}
            testId="today-empty"
          />
        ) : (
          <>
            {signoffs.length > 0 ? <SignoffBlock rows={signoffs} now={sheet.generated_at} /> : null}
            {sheet.funds.length === 0 ? (
              <span className="today-note">No funds on record yet.</span>
            ) : scoped.length === 0 ? (
              <span className="today-note" data-testid="today-scoped-empty">
                That fund is not on today&apos;s sheet.
              </span>
            ) : (
              scoped.map((f) => (
                <FundBlock key={f.id} f={f} today={sheet.today} since={sheet.since} />
              ))
            )}
          </>
        )}
      </div>
    </main>
  )
}
